import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { useAuth } from '@/lib/auth/AuthProvider'
import { cn } from '@/lib/utils'
import { fmtDate, fmtDateTime, fmtRelative } from '../../shared/format'
import { useActiveDemos, useInstallReport, useMeetings, useMyFollowups } from '../api'
import type { WidgetBodyProps } from '../types'
import { WidgetItems, WidgetState } from './WidgetItems'

export function FollowupsWidget({ config }: WidgetBodyProps) {
  const { t } = useTranslation()
  const { can } = useAuth()
  const q = useMyFollowups(can('activities.write'))
  const items = (q.data ?? []).slice(0, 8).map((f) => ({
    id: f.id,
    title: f.account_title,
    subtitle: f.note || f.channel,
    to: `/accounts/${f.account_id}`,
    trailing: <Badge tone={f.overdue ? 'danger' : 'neutral'}>{fmtRelative(f.due_at)}</Badge>,
  }))
  return (
    <WidgetState query={q} empty={items.length === 0} emptyText={t('dashboard.noFollowups')}>
      <WidgetItems items={items} view={config.view} density={config.density} />
    </WidgetState>
  )
}

export function EndingDemosWidget({ config }: WidgetBodyProps) {
  const { t } = useTranslation()
  const { can } = useAuth()
  const q = useActiveDemos(can('accounts.read'))
  const items = [...(q.data ?? [])]
    .sort((a, b) => a.days_left - b.days_left)
    .slice(0, 6)
    .map((d) => ({
      id: d.id,
      title: d.account_title,
      subtitle: fmtDate(d.ends_on),
      to: `/accounts/${d.account_id}`,
      trailing: (
        <Badge tone={d.days_left <= 1 ? 'danger' : d.days_left <= 3 ? 'warning' : 'neutral'}>
          {d.days_left === 0 ? t('dashboard.lastDay') : t('dashboard.daysLeft', { count: d.days_left })}
        </Badge>
      ),
    }))
  return (
    <WidgetState query={q} empty={items.length === 0} emptyText={t('dashboard.noDemos')}>
      <WidgetItems items={items} view={config.view} density={config.density} />
    </WidgetState>
  )
}

export function MeetingsWidget({ config }: WidgetBodyProps) {
  const { t } = useTranslation()
  const q = useMeetings()
  const now = new Date().toISOString()
  const items = (q.data ?? [])
    .filter((m) => m.planned_at >= now)
    .sort((a, b) => a.planned_at.localeCompare(b.planned_at))
    .slice(0, 6)
    .map((m) => ({ id: m.id, title: m.subject, subtitle: m.account_title, to: `/accounts/${m.account_id}`, meta: fmtDateTime(m.planned_at) }))
  return (
    <WidgetState query={q} empty={items.length === 0} emptyText={t('dashboard.widgets.meetings.empty')}>
      <WidgetItems items={items} view={config.view} density={config.density} />
    </WidgetState>
  )
}

export function InstallHealthWidget({ config }: WidgetBodyProps) {
  const { t } = useTranslation()
  const { can } = useAuth()
  const q = useInstallReport(can('reports.read'))
  const compact = config.density === 'compact'
  const rows = [
    ['staleLicenses', q.data?.license_stale],
    ['missingInBinary', q.data?.missing_in_binary],
    ['silent', q.data?.silent_over_24h],
  ] as const
  return (
    <div className={cn('grid h-full grid-cols-1 @sm:grid-cols-3', compact ? 'gap-1.5' : 'gap-2')}>
      {rows.map(([key, value]) => (
        <div key={key} className={cn('rounded-xl border bg-muted/30 dark:bg-muted/15', compact ? 'p-2' : 'p-3')}>
          <p className="truncate text-xs text-muted-foreground">{t(`dashboard.${key}`)}</p>
          {q.isLoading ? (
            <Skeleton className="mt-1.5 h-6 w-10" />
          ) : (
            <p className={cn('mt-0.5 text-xl font-semibold tabular-nums', value ? 'text-destructive' : '')}>{value ?? 0}</p>
          )}
        </div>
      ))}
    </div>
  )
}
